import { useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import Spinner from './Spinner';
import { clampPan, imageOffset, sourceRect } from '../../utils/crop';

const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;
/* Longest side of the JPEG handed back. Avatars render at ≤120px, so this is
   plenty of headroom for retina without shipping a 12-megapixel phone photo. */
const OUTPUT_PX = 640;

const IconZoomOut = () => (
  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M8 12h8" /></svg>
);
const IconZoomIn = () => (
  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M8 12h8M12 8v8" /></svg>
);

/* Copies the visible circle's bounding square out of the decoded <img> into a
   JPEG. The white fill matters: JPEG has no alpha, so a transparent PNG would
   otherwise come out on black. Small photos aren't upscaled past their pixels. */
function cropToBlob(img, { sx, sy, sw, sh }) {
  const out = Math.max(96, Math.min(OUTPUT_PX, Math.round(sw)));
  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, out, out);
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, out, out);
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not crop the photo.'))), 'image/jpeg', 0.9);
  });
}

/* Circular crop for profile photos: drag to reposition, slider to zoom, and
   "Use Photo" hands back a square JPEG File of exactly what shows in the circle.
   Open whenever `file` is set; the caller clears it on cancel/done. */
export default function CropPhotoModal({ file, onCancel, onDone }) {
  const [src, setSrc] = useState('');
  const [natural, setNatural] = useState(null); // { iw, ih } once the photo decodes
  const [failed, setFailed] = useState(false);
  const [size, setSize] = useState(0); // stage side in CSS px, measured
  const [zoom, setZoom] = useState(ZOOM_MIN);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null); // { id, x, y, pan } for the pointer being tracked

  /* one object URL per file, released when the file changes or we unmount */
  useEffect(() => {
    if (!file) {
      setSrc('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setSrc(url);
    setNatural(null);
    setFailed(false);
    setZoom(ZOOM_MIN);
    setPan({ x: 0, y: 0 });
    return () => URL.revokeObjectURL(url);
  }, [file]);

  /* the stage is fluid (fills the modal on phones), so measure rather than assume */
  useEffect(() => {
    const el = stageRef.current;
    if (!el || !file) return undefined;
    const measure = () => setSize(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [file]);

  const ready = !!natural && size > 0 && !failed;

  const changeZoom = (z) => {
    const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
    setZoom(next);
    /* zooming out can leave a gap at an edge — pull the photo back over it */
    if (natural) setPan((p) => clampPan(p, natural.iw, natural.ih, size, next));
  };

  const onPointerDown = (e) => {
    if (!ready) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { id: e.pointerId, x: e.clientX, y: e.clientY, pan };
    setDragging(true);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    setPan(
      clampPan(
        { x: d.pan.x + (e.clientX - d.x), y: d.pan.y + (e.clientY - d.y) },
        natural.iw, natural.ih, size, zoom
      )
    );
  };
  const endDrag = (e) => {
    if (dragRef.current?.id !== e.pointerId) return;
    dragRef.current = null;
    setDragging(false);
  };

  const usePhoto = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(imgRef.current, sourceRect(pan, natural.iw, natural.ih, size, zoom));
      /* keep the person's filename, but it's a JPEG now whatever it was before */
      const base = (file.name || '').replace(/\.[^.]+$/, '') || 'photo';
      onDone(new File([blob], `${base}.jpg`, { type: 'image/jpeg' }));
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const offset = ready ? imageOffset(pan, natural.iw, natural.ih, size, zoom) : null;

  return (
    <Modal
      open={!!file}
      onClose={onCancel}
      title="Crop Photo"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" disabled={!ready || busy} onClick={usePhoto}>
            {busy ? <><Spinner /> Saving…</> : 'Use Photo'}
          </button>
        </>
      }
    >
      {file && (
        <>
          <div
            ref={stageRef}
            className={`crop-stage ${dragging ? 'dragging' : ''}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            {src && !failed && (
              <img
                ref={imgRef}
                src={src}
                alt=""
                draggable={false}
                style={offset ? { left: offset.left, top: offset.top, width: offset.w, height: offset.h } : { visibility: 'hidden' }}
                onLoad={(e) => setNatural({ iw: e.currentTarget.naturalWidth, ih: e.currentTarget.naturalHeight })}
                onError={() => setFailed(true)}
              />
            )}
            {!ready && !failed && <Spinner variant="green" />}
            {failed && <div className="crop-failed">We couldn't read that photo. Try a different one.</div>}
          </div>

          <div className="crop-zoom">
            <button type="button" className="crop-zoom-btn" aria-label="Zoom out" disabled={!ready} onClick={() => changeZoom(zoom - ZOOM_STEP)}>
              <IconZoomOut />
            </button>
            <input
              type="range"
              aria-label="Zoom"
              min={ZOOM_MIN}
              max={ZOOM_MAX}
              step={0.01}
              value={zoom}
              disabled={!ready}
              onChange={(e) => changeZoom(Number(e.target.value))}
            />
            <button type="button" className="crop-zoom-btn" aria-label="Zoom in" disabled={!ready} onClick={() => changeZoom(zoom + ZOOM_STEP)}>
              <IconZoomIn />
            </button>
          </div>
          <p className="crop-hint">Drag the photo to reposition, use the slider to zoom.</p>
        </>
      )}
    </Modal>
  );
}
